import React, { useCallback } from 'react'
import { GenericSearch } from 'app/views/components/search/generic-search'
import { TradeSearchTable } from './trade-search-table'
import { MasterTradeParams } from 'app/models/master-trade-params'
import { useNavigate } from 'react-router-dom'

interface Props {
  data: MasterTradeParams[]
  placeholder?: string
  /**
   * Called while there is a search value (after render, on every change): a
   * page that does not hold the trades up front loads them here, guarding
   * itself against a repeat once they are in.
   */
  onSearchStart?: () => void
  /** Those trades are still on their way: the dropdown shows a spinner instead of "no matching trades". */
  loading?: boolean
}

export function TradeSearch({ data, placeholder = 'Trade Search', onSearchStart, loading }: Props) {
  const navigate = useNavigate()

  const navigateToTrade = useCallback(
    (tradeId: string) => {
      if (tradeId) {
        navigate(`/tradeentry/${tradeId}`)
      }
    },
    [navigate],
  )

  const handleTradeSelect = useCallback(
    (trade: MasterTradeParams) => {
      navigateToTrade(trade.id || '')
    },
    [navigateToTrade],
  )

  const renderTradeTable = useCallback(
    (
      filteredData: MasterTradeParams[],
      searchValue: string,
      onItemSelect: (trade: MasterTradeParams) => void,
      onClose: () => void,
    ) => {
      return (
        <TradeSearchTable
          data={filteredData}
          searchValue={searchValue}
          onTradeSelect={onItemSelect}
          onClose={onClose}
          loading={loading}
          onSearchStart={onSearchStart}
        />
      )
    },
    [onSearchStart, loading],
  )

  return (
    <GenericSearch
      data={data}
      placeholder={placeholder}
      searchableFields={['systemId', 'clientId']}
      onItemSelect={handleTradeSelect}
      renderTable={renderTradeTable}
      width={300}
      className="generic-search"
      storageKey="trade-search-value"
    />
  )
}
